import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

const DEFAULT_MESSAGE = "You've reached your plan's limit. Upgrade your plan to unlock more.";

/**
 * Reusable disabled-state + upgrade-tooltip control, extracted from the
 * disabled-button/Radix-Tooltip pattern in pages/SchoolSettings.jsx. Renders
 * a Button that shows an upgrade tooltip (with a link to the account
 * subscription page) only while disabled, and never fires onClick while
 * disabled.
 *
 * audience="sub" suppresses the Upgrade link entirely — Subs (TOCs) are not
 * plan buyers, so their tooltips must never contain upsell copy (see
 * usePlanLimits.subFeatureMessage for the neutral Sub-facing wording).
 *
 * The employer link targets /employer/subscription (the authenticated
 * plan-management page with the real checkout/upgrade flow), not the public
 * /pricing marketing page — a logged-in employer clicking "Upgrade" should
 * land where they can actually upgrade.
 */
export default function PlanLimitTooltip({
  disabled,
  // Callers that also disable the control for an unrelated reason (e.g. an
  // empty input, a request in flight) can pass this separately so the
  // upgrade tooltip only appears when the plan limit is the actual cause.
  showTooltip = disabled,
  message = DEFAULT_MESSAGE,
  audience = 'employer',
  upgradeTo = '/employer/subscription',
  onClick,
  children,
  ...buttonProps
}) {
  const handleClick = (event) => {
    // Never perform the restricted action while disabled — mirrors the guard
    // style already used for the "last active school" archive button.
    if (disabled) return;
    onClick?.(event);
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block">
            <Button {...buttonProps} disabled={disabled} onClick={handleClick}>
              {children}
            </Button>
          </span>
        </TooltipTrigger>
        {showTooltip && (
          <TooltipContent>
            <p>
              {message}
              {audience === 'employer' && (
                <>
                  {' '}
                  <Link to={upgradeTo} className="underline font-medium">
                    Upgrade
                  </Link>
                </>
              )}
            </p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}
